import { MousePointerClick } from 'lucide-preact'
import type { BlastEngagementDto } from '@/types/emailBlast'
import { formatRate } from './engagementFormat'
import { SectionHeading } from './blastDetailSections'

const OPEN_RATE_CAVEAT =
  'Apple Mail Privacy auto-loads the open pixel, so open rate is inflated. Treat clicks as the reliable signal.'

/**
 * Blast engagement section (ORB-384): a slim measure row with clicks privileged (primary, large) and
 * open rate muted + caveated, the approximate in-window unsubscribe line, and the top clicked links.
 * When nothing has been delivered yet, rates read "—" (never a misleading 0%); a measured zero shows 0%.
 */
export function BlastEngagementSection({ engagement }: { engagement: BlastEngagementDto | null | undefined }) {
  const hasData = !!engagement && engagement.Delivered > 0

  return (
    <section className="space-y-4">
      <SectionHeading>Engagement</SectionHeading>

      {!hasData ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Nothing delivered yet — open and click rates appear once delivery starts. Rates show “—”, never a
          misleading 0%.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <Measure value={engagement!.Delivered.toLocaleString()} label="delivered" />
            <Divider />
            <Measure value={formatRate(engagement!.ClickRatePercent)} label="click rate" size="lg" privileged />
            <Measure value={formatRate(engagement!.ClickToOpenRatePercent)} label="click-to-open" privileged />
            <Divider />
            <Measure value={formatRate(engagement!.OpenRatePercent)} label="open rate" muted caveat={OPEN_RATE_CAVEAT} />
            <Measure value={formatRate(engagement!.BounceRatePercent)} label="bounce rate" muted />
          </div>

          {engagement!.ApproxUnsubscribesInWindow != null && (
            <p className="text-xs text-slate-400 dark:text-slate-500">
              <span className="font-medium text-slate-500 dark:text-slate-400">
                {engagement!.ApproxUnsubscribesInWindow.toLocaleString()}
              </span>{' '}
              {engagement!.ApproxUnsubscribesInWindow === 1 ? 'recipient' : 'recipients'} unsubscribed within{' '}
              {engagement!.UnsubscribeWindowDays} days of this send (from the suppression list; approximate
              attribution). Campaign-level unsubscribe rate is on the campaign Broadcasts tab.
            </p>
          )}

          {engagement!.TopClickedLinks.length > 0 && (
            <div className="pt-1">
              <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                <MousePointerClick className="w-3.5 h-3.5" /> Top clicked links
                <span className="font-normal normal-case text-slate-400 dark:text-slate-500">· last click per recipient</span>
              </h4>
              <ul className="space-y-1.5 text-sm">
                {engagement!.TopClickedLinks.map(link => (
                  <li key={link.Url} className="flex items-center justify-between gap-3">
                    <span className="truncate text-slate-700 dark:text-slate-300" title={link.Url}>{link.Url}</span>
                    <span className="shrink-0 tabular-nums font-medium text-primary-700 dark:text-primary-400">
                      {link.Count.toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </section>
  )
}

function Divider() {
  return <div className="h-9 w-px bg-slate-200 dark:bg-slate-700/70" aria-hidden="true" />
}

function Measure({
  value,
  label,
  size = 'md',
  privileged = false,
  muted = false,
  caveat,
}: {
  value: string
  label: string
  size?: 'md' | 'lg'
  privileged?: boolean
  muted?: boolean
  caveat?: string
}) {
  const valueClass = privileged
    ? `${size === 'lg' ? 'text-3xl' : 'text-2xl'} font-semibold tabular-nums text-primary-700 dark:text-primary-400`
    : muted
    ? 'text-xl font-medium tabular-nums text-slate-500 dark:text-slate-400'
    : 'text-2xl font-semibold tabular-nums text-slate-900 dark:text-white'
  const labelClass = privileged
    ? 'text-xs uppercase tracking-wide text-primary-700/70 dark:text-primary-400/70'
    : 'text-xs uppercase tracking-wide text-slate-400 dark:text-slate-500'
  return (
    <div className="flex items-baseline gap-1.5">
      <span className={valueClass}>{value}</span>
      <span
        className={caveat ? `${labelClass} underline decoration-dotted underline-offset-2 cursor-help` : labelClass}
        title={caveat}
      >
        {label}
        {caveat ? ' ⓘ' : ''}
      </span>
    </div>
  )
}
